import axios from 'axios'

// ===========================================
// Token 管理（使用 Cookie 持久化存储）
// ===========================================
// Token 存储 Key（用于 Cookie）
const TOKEN_KEY = 'auth:token'
const USER_INFO_KEY = 'userInfo'

// Cookie 有效期：30天（与后端 Token 有效期一致）
const COOKIE_MAX_AGE = 30 * 24 * 60 * 60 // 30天，单位秒

/**
 * 设置 Cookie
 * @param name Cookie 名称
 * @param value Cookie 值
 * @param maxAge 有效期（秒）
 */
function setCookie(name, value, maxAge) {
    const expires = new Date(Date.now() + maxAge * 1000).toUTCString()
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`
}

/**
 * 获取 Cookie
 * @param name Cookie 名称
 * @returns Cookie 值，没有则返回 null
 */
function getCookie(name) {
    const cookies = document.cookie.split('; ')
    for (const cookie of cookies) {
        const [key, val] = cookie.split('=')
        if (key === name) {
            return decodeURIComponent(val)
        }
    }
    return null
}

/**
 * 删除 Cookie
 * @param name Cookie 名称
 */
function removeCookie(name) {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
}

/**
 * 获取当前保存的 Token
 * 优先从 Cookie 获取，兼容旧的 localStorage/sessionStorage
 */
function getToken() {
    // 优先从 Cookie 获取
    const cookieToken = getCookie(TOKEN_KEY)
    if (cookieToken) return cookieToken
    // 兼容旧数据
    return localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY)
}

/**
 * 保存 Token 到 Cookie（30天有效期）
 * @param token Token 字符串
 * @param remember 是否记住我（现在统一存 Cookie，remember 不再区分存储方式）
 */
function setToken(token, remember) {
    // 统一存入 Cookie，30天有效
    setCookie(TOKEN_KEY, token, COOKIE_MAX_AGE)
    // 同时存入 localStorage 作为备份
    localStorage.setItem(TOKEN_KEY, token)
}

/**
 * 清除 Token（退出登录时调用）
 */
function clearToken() {
    removeCookie(TOKEN_KEY)
    localStorage.removeItem(TOKEN_KEY)
    sessionStorage.removeItem(TOKEN_KEY)
}

/**
 * 获取用户信息（从 Cookie 或 localStorage）
 */
function getUserInfo() {
    const cookieUser = getCookie(USER_INFO_KEY)
    if (cookieUser) {
        try { return JSON.parse(cookieUser) } catch (e) { /* ignore */ }
    }
    const stored = localStorage.getItem(USER_INFO_KEY) || sessionStorage.getItem(USER_INFO_KEY)
    if (stored) {
        try { return JSON.parse(stored) } catch (e) { /* ignore */ }
    }
    return null
}

/**
 * 保存用户信息到 Cookie
 */
function setUserInfo(userInfo) {
    setCookie(USER_INFO_KEY, JSON.stringify(userInfo), COOKIE_MAX_AGE)
    localStorage.setItem(USER_INFO_KEY, JSON.stringify(userInfo))
}

/**
 * 清除用户信息
 */
function clearUserInfo() {
    removeCookie(USER_INFO_KEY)
    localStorage.removeItem(USER_INFO_KEY)
    sessionStorage.removeItem(USER_INFO_KEY)
}

// ===========================================
// Axios 实例配置
// ===========================================
const service = axios.create({
    // baseURL 为空，因为后端接口本身已经包含 /api 前缀（如 /api/types、/api/auth/login）
    // 开发环境走 vue.config.js proxy，生产环境走 nginx proxy
    baseURL: '',
    timeout: 20000,                    // 请求超时时间：20秒
    withCredentials: true              // 保持跨域 Cookie
})

// ===========================================
// 请求拦截器（自动添加 Token 到 Header）
// ===========================================
service.interceptors.request.use(
    config => {
        const token = getToken()
        if (token) {
            config.headers.Authorization = 'Bearer ' + token
        }
        return config
    },
    error => {
        console.log(error)
        return Promise.reject(error)
    }
)

// ===========================================
// 响应拦截器（自动处理 Token）
// ===========================================
service.interceptors.response.use(
    response => {
        const res = response.data

        // 防御性处理：确保 res 至少是一个对象
        if (res === undefined || res === null) {
            console.warn('API 返回空响应:', response.config.url)
            return { success: false, message: '服务器返回空响应' }
        }

        // 登录成功时自动保存 Token
        if (res && res.success && res.token && response.config.url === '/api/auth/login') {
            let remember = false
            try {
                const requestData = typeof response.config.data === 'string'
                    ? JSON.parse(response.config.data)
                    : response.config.data
                remember = requestData && requestData.remember
            } catch (e) {
                console.warn('解析请求数据失败:', e)
            }
            console.log('登录成功，保存 Token 到 Cookie，remember:', remember)
            setToken(res.token, remember)

            // 同时保存用户信息到 Cookie
            if (res.user) {
                setUserInfo({
                    username: res.user.username,
                    nickname: res.user.nickname,
                    avatar: res.user.avatar,
                    email: res.user.email
                })
            }
        }

        // 退出登录时清除 Token
        if (response.config.url === '/api/auth/logout') {
            clearToken()
            clearUserInfo()
        }

        return res
    },
    error => {
        if (error.response && error.response.status === 401) {
            console.error('未授权，请重新登录')
            clearToken()
            clearUserInfo()
            return Promise.reject({
                success: false,
                message: (error.response.data && error.response.data.message) || '未授权，请重新登录'
            })
        }

        console.error('Request Error:', error)
        return Promise.reject(error)
    }
)

// 导出 Token 管理函数供其他模块使用
export { getToken, setToken, clearToken, getUserInfo, setUserInfo, clearUserInfo }

// 导出 axios 实例
export default service
